import React, {useEffect, useState} from 'react';
import {useTranslation} from "react-i18next";
import {useNavigate, useParams} from "react-router-dom";
import {useServerError} from "../../../../hooks/useServerError";
import apiAxios from "../../../../app/api/apiAxios";
import ResultCode from "../../../../data/enums/ResultCode";
import getServerErrorResourceName from "../../../../data/apiErrors/apiErrors";
import {toast} from "react-toastify";
import FormLayout from "../../../ui/layouts/FormLayout/FormLayout";
import Button, {ButtonType} from "../../../ui/components/Button/Button";
import PostImageTemplateForm from "./PostImageTemplateForm";

const initialState = {
    id: 0,
    name: '',
    isBlocked: false,
    icon: null,
    iconName: '',
    iconSize: null
}

const PostImageTemplate = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {id} = useParams();
    const [isLoading, setIsLoading] = useState(false);
    const [postImageTemplate, setPostImageTemplate] = useState(initialState);
    const [serverError, setServerError] = useServerError(null);

    useEffect(() => {
        (async () => await load(id))();
    }, [id]);

    const load = async (id) => {
        if (id && id > 0) {
            setServerError(null);
            setPostImageTemplate(initialState);
            setIsLoading(true);

            const url = `${process.env.REACT_APP_BE_DOMAIN}/api/postImageTemplate/getPostImageTemplate?id=${id}`;

            try {
                const response = await apiAxios.get(url);

                if (response) {
                    if (response.status === 200 && response.data) {
                        if (response.data.resultCode === ResultCode.Success) {
                            setPostImageTemplate(response.data);
                        } else {
                            setServerError(t(getServerErrorResourceName({
                                model: 'postImageTemplate',
                                resultCode: response.data.resultCode
                            })));
                        }
                    }
                }

                setIsLoading(false);
            } catch (e) {
                if (e && e.message) {
                    setServerError(`${t(getServerErrorResourceName({
                        model: 'postImageTemplate',
                        resultCode: ResultCode.ServerError
                    }))} Message: (${e.message})`);
                }
                setIsLoading(false);
            }
        }
    }

    const save = async (values, actions) => {
        try {
            setServerError(null);
            setIsLoading(true);
            actions.setSubmitting(true);

            const response = await apiAxios.post(`${process.env.REACT_APP_BE_DOMAIN}/api/postImageTemplate/save`, {
                id: postImageTemplate.id,
                name: values.name,

                isBlocked: values.isBlocked,

                logo: values.image.image,
                logoSize: values.image.size,
                logoName: values.image.name
            });

            if (response) {
                if (response.status === 200 && response.data) {
                    if (response.data.resultCode === ResultCode.Success) {
                        toast.success(t("postImageTemplate.form.saved-label"));

                        if (response.data.description) {
                            if (!id || id <= 0) {
                                await load(+response.data.description);
                            }
                        }
                    } else {
                        setServerError(t(getServerErrorResourceName({
                            model: 'postImageTemplate',
                            resultCode: response.data.resultCode,
                            description: response.data.resultCode === ResultCode.ServerError ? null : response.data.description
                        })));
                    }
                }
            }

            setIsLoading(false);
            actions.setSubmitting(false);
        } catch (err) {
            if (err && err.message) {
                setServerError(`${t(getServerErrorResourceName({
                    model: 'postImageTemplate',
                    resultCode: ResultCode.ServerError
                }))} Message: (${err.message})`);
            }
            setIsLoading(false);
            actions.setSubmitting(false);
        }
    }

    return (
        <FormLayout header={<div className='default-form-layout-header'>{t("postImageTemplate.form.form-label")}</div>}
                    footer={(
                        <div className='default-form-layout-footer'>
                            <Button buttonType={ButtonType.Back}
                                    onClick={() => navigate(-1)}
                                    disabled={isLoading}>
                                {t("btn.back")}
                            </Button>
                            <Button buttonType={ButtonType.Default}
                                    form='post-image-template-form'
                                    type='submit'
                                    disabled={isLoading}>
                                {t("btn.save")}
                            </Button>
                        </div>
                    )}
                    footerPosition='top'
                    isLoading={isLoading}>
            <div className='default-form-layout-container'>
                {serverError &&
                    <div className='server-error-container'>
                        <div>
                            {serverError}
                        </div>
                    </div>
                }
                <PostImageTemplateForm {...postImageTemplate} onSubmit={save}/>
            </div>
        </FormLayout>
    );
};

export default PostImageTemplate;
